import styled from "@emotion/styled";
import { Button, Callout, H3, IconChevron, Link, List, Text } from "atoms";
import { ArtistCard, useArtist } from "features/reviewable";
import { useToggle } from "utils/hooks";
import { useUserFollows } from "../api/useFollows";
import { FollowButton } from "./FollowButton";

type UserFollowsProps = {
  userId: string;
  allowUpdates: boolean;
};

function UserFollows({ userId, allowUpdates }: UserFollowsProps) {
  const { data } = useUserFollows(userId);

  const [isOpen, toggleisOpen] = useToggle(false);

  const truncateIndex = 6;

  if (!data) {
    return null;
  }

  const isFollowingArtists = data.artistIds.length > 0;
  const artistsShowing = isOpen
    ? data.artistIds
    : data.artistIds.slice(0, truncateIndex);

  return !isFollowingArtists ? (
    <Callout center>
      <H3 size="md">You aren&apos;t following any Artists</H3>
      <Text>
        You can be notified of drops, events, and other announcements posted by
        artists you&nbsp;follow.
      </Text>
      <Link variant="primary" to="/search/?type=artist" size="sm" arrow="r">
        Find artists to follow
      </Link>
    </Callout>
  ) : (
    <>
      <SList type="blank">
        {artistsShowing.map((artistId) => (
          <FollowItem
            key={artistId}
            artistId={artistId}
            allowUpdates={allowUpdates}
          />
        ))}
      </SList>
      {data.artistIds.length > truncateIndex && (
        <Button onClick={toggleisOpen} size="xs" variant="blank">
          <IconChevron style={{ transform: isOpen ? "" : "rotate(180deg)" }} />
          <span>{isOpen ? "Collapse" : "Show all"}</span>
        </Button>
      )}
    </>
  );
}

type FollowItemProps = {
  artistId: string;
  allowUpdates: boolean;
};

function FollowItem({ artistId, allowUpdates }: FollowItemProps) {
  const { data: artist } = useArtist(artistId);

  if (!artist) {
    return null;
  }
  return (
    <li>
      <ArtistCard
        key={artistId}
        {...artist}
        isSmall
        followButton={
          allowUpdates ? (
            <SFollowButton unfollowText="Unfollow" small artistId={artistId} />
          ) : null
        }
      />
    </li>
  );
}

const minSize = "270px";
const SList = styled(List)(({ theme }) => ({
  marginBottom: theme.spacing.gap,
  display: "grid",
  gap: theme.spacing.gap,
  gridTemplateColumns: "100%",
  "@media (min-width: 580px)": {
    gridTemplateColumns: `repeat(auto-fill, minmax(min(${minSize}, 50% - 1rem), 1fr))`,
  },
  "&&>li": {
    border: 0,
    padding: 0,
    margin: 0,
  },
}));

const SFollowButton = styled(FollowButton)(({ theme }) => ({
  position: "unset",
  '&[type="button"]': {
    position: "relative",
    zIndex: 10,
  },
  ".Tooltip": {
    position: "static",
  },
  ".TooltipContent": {
    backgroundColor: theme.colors.bg,
    boxShadow: "none",
    top: "auto",
    left: 10,
    right: 10,
    bottom: 10,
    maxWidth: "calc(100% - 20px)",
    minWidth: 0,
    padding: "30px 10px 10px 10px",
    textAlign: "center",
    transform: "none",
    zIndex: 600,

    "&::before, &::after": {
      content: "none",
    },
    "div:last-of-type": {
      justifyContent: "stretch",
      a: { width: "100%" },
    },
  },
  "&>button": {
    position: "relative",
    zIndex: 10,
  },
  "button::before, button::after": {
    content: "none",
  },
}));

export { UserFollows };
